import React, { useState, useEffect } from 'react';

const API = process.env.REACT_APP_API_URL || '';

// ── Color tokens ──────────────────────────────────────────────────────────────
const LABEL_COLORS = {
  'Blue Chip':  'var(--accent)',
  'Premium':    '#a855f7',
  'Solid':      '#00e676',
  'Volatile':   '#ffd32a',
  'Speculative':'#8b90a0',
  'Throw-In':   '#555b6e',
  'Declining':  'var(--accent2)',
};

const CONF_COLORS = {
  High:   '#00e676',
  Medium: '#ffd32a',
  Low:    '#ff4757',
};

const TREND_ICONS = {
  up:     { icon: '↑', color: '#00e676' },
  down:   { icon: '↓', color: '#ff4757' },
  stable: { icon: '→', color: '#8b90a0' },
};

const CHEST_COLORS = {
  'Blue-Chip Loaded':      'var(--accent)',
  'Rebuild Arsenal':       '#a855f7',
  'Balanced Capital':      '#00e676',
  'Win-Now Empty Cabinet': '#ff4757',
  'Volatile Portfolio':    '#ffd32a',
};

function Avatar({ avatar, name, size = 28 }) {
  if (avatar)
    return <img src={`https://sleepercdn.com/avatars/thumbs/${avatar}`} alt={name} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover' }} onError={e => (e.target.style.display = 'none')} />;
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: 'var(--bg4)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.38, color: 'var(--text3)', fontWeight: 700 }}>
      {(name || '?').slice(0, 2).toUpperCase()}
    </div>
  );
}

// ── Pick Card ─────────────────────────────────────────────────────────────────
function PickCard({ pick, compact = false }) {
  const labelColor = LABEL_COLORS[pick.market_label] || '#888';
  const confColor = CONF_COLORS[pick.confidence] || '#888';
  const trend = TREND_ICONS[pick.trend] || TREND_ICONS.stable;

  return (
    <div style={{
      background: 'var(--bg2)',
      border: `1px solid ${labelColor}22`,
      borderLeft: `3px solid ${labelColor}`,
      borderRadius: 8,
      padding: compact ? '10px 12px' : '14px 16px',
      position: 'relative',
    }}>
      {/* Header row */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 6 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: compact ? '1rem' : '1.15rem', fontWeight: 800, color: 'var(--text)', letterSpacing: 1 }}>
              {pick.slot_label}
            </span>
            <span style={{ fontSize: '0.6rem', padding: '2px 7px', borderRadius: 4, background: labelColor + '18', color: labelColor, fontFamily: 'var(--font-mono)', fontWeight: 700 }}>
              {pick.market_label}
            </span>
            <span style={{ color: trend.color, fontSize: '0.85rem', fontWeight: 700 }} title={`Trend: ${pick.trend}`}>
              {trend.icon}
            </span>
          </div>
          <div style={{ fontSize: '0.62rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>
            Max PF: {(pick.max_pf || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} · Range: {pick.slot_range_label}
          </div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: '1.1rem', fontWeight: 800, color: 'var(--accent)' }}>
            {pick.market_value}
          </div>
          <div style={{ fontSize: '0.6rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)' }}>pts</div>
        </div>
      </div>

      {/* NFL/Madden-style ownership: current holder first, source shown as "via". */}
      <div style={{ marginBottom: compact ? 0 : 8 }}>
        <div>
          <div style={{ fontSize: '0.58rem', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2, fontFamily: 'var(--font-mono)' }}>Pick Holder</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <Avatar avatar={pick.current_owner_avatar} name={pick.current_owner_display_name} size={20} />
            <span style={{ fontSize: '0.75rem', color: pick.is_traded ? 'var(--accent)' : 'var(--text2)', fontWeight: 600 }}>{pick.current_owner_display_name}</span>
            {pick.is_traded && <span style={{ fontSize: '0.65rem', color: 'var(--text3)' }}>(via {pick.original_owner_display_name})</span>}
          </div>
        </div>
      </div>

      {/* Confidence + explanation */}
      {!compact && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
            <span style={{ fontSize: '0.6rem', padding: '1px 6px', borderRadius: 4, background: confColor + '18', color: confColor, fontFamily: 'var(--font-mono)' }}>
              {pick.confidence} Confidence
            </span>
            <span style={{ fontSize: '0.6rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)' }}>
              {pick.year} · Round {pick.round}
            </span>
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text3)', lineHeight: 1.5 }}>
            {pick.explanation}
          </div>
        </>
      )}
    </div>
  );
}

// ── Market Stat Card ──────────────────────────────────────────────────────────
function StatCard({ label, value, sub, color = 'var(--accent)' }) {
  return (
    <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px' }}>
      <div style={{ fontSize: '0.58rem', color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 1.5, fontFamily: 'var(--font-mono)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: '1.1rem', fontWeight: 800, color }}>{value}</div>
      {sub && <div style={{ fontSize: '0.65rem', color: 'var(--text3)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ── Market Overview ───────────────────────────────────────────────────────────
function MarketOverview({ overview }) {
  const ov = overview || {};
  return (
    <section style={{ marginBottom: 28 }}>
      <h2 style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 2, color: 'var(--text3)', fontFamily: 'var(--font-mono)', marginBottom: 12 }}>Market Overview</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10, marginBottom: 16 }}>
        <StatCard label="Blue Chips" value={ov.blue_chip_count ?? '—'} sub="Projected 1.01–1.03 picks" color="var(--accent)" />
        <StatCard label="Picks Tracked" value={ov.total_picks_tracked ?? '—'} sub={`${ov.picks_traded ?? 0} traded`} />
        {ov.most_valuable_pick && (
          <StatCard
            label="Most Valuable Pick"
            value={ov.most_valuable_pick.slot_label}
            sub={`${ov.most_valuable_pick.market_value} pts · ${ov.most_valuable_pick.current_owner_display_name}`}
            color={LABEL_COLORS[ov.most_valuable_pick.market_label] || '#888'}
          />
        )}
        {ov.strongest_portfolio && (
          <StatCard
            label="Strongest Portfolio"
            value={ov.strongest_portfolio.display_name}
            sub={`${ov.strongest_portfolio.total_value} pts total`}
            color="#a855f7"
          />
        )}
        {ov.biggest_riser && (
          <StatCard
            label="Biggest Riser"
            value={ov.biggest_riser.slot_label}
            sub={`${ov.biggest_riser.original_owner_display_name}'s pick — trending up`}
            color="var(--green)"
          />
        )}
        {ov.biggest_faller && (
          <StatCard
            label="Biggest Faller"
            value={ov.biggest_faller.slot_label}
            sub={`${ov.biggest_faller.original_owner_display_name}'s pick — trending down`}
            color="var(--red)"
          />
        )}
        {ov.highest_risk_outgoing && (
          <StatCard
            label="Highest Risk Outgoing"
            value={ov.highest_risk_outgoing.slot_label}
            sub={`${ov.highest_risk_outgoing.original_owner_display_name} traded away`}
            color="#ffd32a"
          />
        )}
      </div>
    </section>
  );
}

// ── Portfolio Rankings ─────────────────────────────────────────────────────────
function PortfolioRankings({ portfolios }) {
  return (
    <section style={{ marginBottom: 28 }}>
      <h2 style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 2, color: 'var(--text3)', fontFamily: 'var(--font-mono)', marginBottom: 12 }}>Pick Portfolio Rankings</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {(portfolios || []).map((port, i) => (
          <div key={port.roster_id} style={{
            display: 'grid', gridTemplateColumns: '28px 32px 1fr auto auto auto',
            alignItems: 'center', gap: 10,
            background: 'var(--bg2)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px',
          }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', color: i < 3 ? '#ffd32a' : 'var(--text3)', fontWeight: 700 }}>#{i + 1}</span>
            <Avatar avatar={port.avatar} name={port.display_name} size={28} />
            <span style={{ fontSize: '0.82rem', fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{port.display_name}</span>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.78rem', fontWeight: 700, color: 'var(--accent)' }}>{port.blue_chip_count}</div>
              <div style={{ fontSize: '0.55rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)' }}>blue chips</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.78rem', fontWeight: 700, color: 'var(--text2)' }}>{port.total_1sts}</div>
              <div style={{ fontSize: '0.55rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)' }}>1st rounds</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontFamily: 'var(--font-mono)', fontSize: '0.9rem', fontWeight: 800, color: 'var(--accent)' }}>{port.total_value}</div>
              <div style={{ fontSize: '0.55rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)' }}>pts</div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── War Chests ────────────────────────────────────────────────────────────────
function WarChests({ warChests }) {
  return (
    <section style={{ marginBottom: 28 }}>
      <h2 style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 2, color: 'var(--text3)', fontFamily: 'var(--font-mono)', marginBottom: 12 }}>Manager War Chests</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
        {(warChests || []).map(chest => {
          const labelColor = CHEST_COLORS[chest.war_chest_label] || '#888';
          return (
            <div key={chest.roster_id} style={{
              background: 'var(--bg2)', border: `1px solid ${labelColor}22`,
              borderTop: `3px solid ${labelColor}`, borderRadius: 8, padding: '14px 16px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Avatar avatar={chest.avatar} name={chest.display_name} size={28} />
                <div>
                  <div style={{ fontSize: '0.82rem', fontWeight: 700 }}>{chest.display_name}</div>
                  <div style={{ fontSize: '0.6rem', padding: '1px 6px', borderRadius: 4, background: labelColor + '18', color: labelColor, fontFamily: 'var(--font-mono)', display: 'inline-block', marginTop: 2 }}>
                    {chest.war_chest_label}
                  </div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, fontSize: '0.7rem' }}>
                <div style={{ textAlign: 'center', background: 'var(--bg3)', borderRadius: 6, padding: '6px 4px' }}>
                  <div style={{ fontWeight: 800, color: 'var(--accent)', fontFamily: 'var(--font-mono)' }}>{chest.total_value}</div>
                  <div style={{ color: 'var(--text3)', fontSize: '0.58rem' }}>total value</div>
                </div>
                <div style={{ textAlign: 'center', background: 'var(--bg3)', borderRadius: 6, padding: '6px 4px' }}>
                  <div style={{ fontWeight: 800, color: 'var(--accent)', fontFamily: 'var(--font-mono)' }}>{chest.blue_chip_picks?.length ?? 0}</div>
                  <div style={{ color: 'var(--text3)', fontSize: '0.58rem' }}>blue chips</div>
                </div>
                <div style={{ textAlign: 'center', background: 'var(--bg3)', borderRadius: 6, padding: '6px 4px' }}>
                  <div style={{ fontWeight: 800, color: chest.dangerous_outgoing?.length ? 'var(--red)' : 'var(--text3)', fontFamily: 'var(--font-mono)' }}>
                    {chest.own_picks_outgoing?.length ?? 0}
                  </div>
                  <div style={{ color: 'var(--text3)', fontSize: '0.58rem' }}>picks owed</div>
                </div>
              </div>
              {chest.dangerous_outgoing?.length > 0 && (
                <div style={{ marginTop: 8, padding: '6px 8px', background: 'rgba(255,71,87,0.08)', borderRadius: 6, border: '1px solid rgba(255,71,87,0.2)' }}>
                  <div style={{ fontSize: '0.6rem', color: 'var(--red)', fontFamily: 'var(--font-mono)', marginBottom: 3 }}>⚠️ Dangerous outgoing</div>
                  {chest.dangerous_outgoing.map(p => (
                    <div key={p.pick_id} style={{ fontSize: '0.68rem', color: 'var(--text2)' }}>
                      {p.slot_label} owed to {p.current_owner_display_name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ── Original Owner Risk Board ─────────────────────────────────────────────────
function RiskBoard({ riskBoard }) {
  if (!riskBoard?.length) return null;
  return (
    <section style={{ marginBottom: 28 }}>
      <h2 style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 2, color: 'var(--text3)', fontFamily: 'var(--font-mono)', marginBottom: 12 }}>Original Owner Risk Board</h2>
      <div style={{ fontSize: '0.7rem', color: 'var(--text3)', marginBottom: 10 }}>Managers whose picks others are holding — ranked by total value owed</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {riskBoard.map(entry => (
          <div key={entry.roster_id} style={{ background: 'var(--bg2)', border: '1px solid rgba(255,71,87,0.15)', borderRadius: 8, padding: '12px 14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Avatar avatar={entry.avatar} name={entry.display_name} size={24} />
                <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>{entry.display_name}</span>
                <span style={{ fontSize: '0.6rem', color: 'var(--text3)', fontFamily: 'var(--font-mono)' }}>{entry.outgoing_count} pick{entry.outgoing_count !== 1 ? 's' : ''} owed</span>
              </div>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.82rem', fontWeight: 700, color: 'var(--accent2)' }}>{entry.total_outgoing_value} pts</span>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {entry.outgoing_picks.map(p => (
                <div key={p.pick_id} style={{
                  padding: '3px 8px', borderRadius: 4,
                  background: (LABEL_COLORS[p.market_label] || '#888') + '12',
                  border: `1px solid ${(LABEL_COLORS[p.market_label] || '#888')}22`,
                  fontSize: '0.65rem', fontFamily: 'var(--font-mono)',
                  color: LABEL_COLORS[p.market_label] || '#888',
                }}>
                  {p.slot_label} → {p.current_owner_display_name}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Draft Board ───────────────────────────────────────────────────────────────
function DraftBoard({ draftBoard, activeYear, onYearChange, futureYears }) {
  const [activeRound, setActiveRound] = useState('1');
  const yearPicks = (draftBoard || {})[String(activeYear)] || {};
  const roundPicks = yearPicks[activeRound] || [];

  return (
    <section style={{ marginBottom: 28 }}>
      <h2 style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 2, color: 'var(--text3)', fontFamily: 'var(--font-mono)', marginBottom: 12 }}>Future Draft Board</h2>

      {/* Year tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
        {(futureYears || []).map(yr => (
          <button key={yr} onClick={() => onYearChange(yr)} style={{
            background: activeYear === yr ? 'var(--accent)' : 'var(--bg3)',
            border: 'none', borderRadius: 6, color: activeYear === yr ? '#000' : 'var(--text2)',
            padding: '5px 14px', fontSize: '0.78rem', fontWeight: 600,
            cursor: 'pointer', fontFamily: 'var(--font-mono)',
          }}>{yr}</button>
        ))}
      </div>

      {/* Round tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 12 }}>
        {['1', '2', '3'].map(r => (
          <button key={r} onClick={() => setActiveRound(r)} style={{
            background: activeRound === r ? 'var(--bg4)' : 'var(--bg3)',
            border: `1px solid ${activeRound === r ? 'var(--border)' : 'transparent'}`,
            borderRadius: 5, color: activeRound === r ? 'var(--text)' : 'var(--text3)',
            padding: '4px 10px', fontSize: '0.72rem', fontWeight: 500,
            cursor: 'pointer', fontFamily: 'var(--font-mono)',
          }}>Round {r}</button>
        ))}
      </div>

      {roundPicks.length === 0 ? (
        <div style={{ fontSize: '0.78rem', color: 'var(--text3)', padding: 16 }}>No picks for this year/round.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
          {roundPicks.map(pick => <PickCard key={pick.pick_id} pick={pick} />)}
        </div>
      )}
    </section>
  );
}

// ── Blue Chips ────────────────────────────────────────────────────────────────
function BlueChips({ picks }) {
  if (!picks?.length)
    return (
      <section style={{ marginBottom: 28 }}>
        <h2 style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 2, color: 'var(--text3)', fontFamily: 'var(--font-mono)', marginBottom: 12 }}>Blue Chip Picks</h2>
        <div style={{ fontSize: '0.78rem', color: 'var(--text3)' }}>No blue chip picks identified — no team is projecting bottom-2 with medium/high confidence.</div>
      </section>
    );
  return (
    <section style={{ marginBottom: 28 }}>
      <h2 style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 2, color: 'var(--accent)', fontFamily: 'var(--font-mono)', marginBottom: 12 }}>Blue Chip Picks</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
        {picks.map(p => <PickCard key={p.pick_id} pick={p} />)}
      </div>
    </section>
  );
}

// ── Risers / Fallers ──────────────────────────────────────────────────────────
function RisersFallers({ risers, fallers }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 28 }}>
      <section>
        <h2 style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 2, color: 'var(--green)', fontFamily: 'var(--font-mono)', marginBottom: 10 }}>Biggest Risers</h2>
        {!risers?.length ? (
          <div style={{ fontSize: '0.75rem', color: 'var(--text3)' }}>No picks on an upward trend right now.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {risers.map(p => <PickCard key={p.pick_id} pick={p} compact />)}
          </div>
        )}
      </section>
      <section>
        <h2 style={{ fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: 2, color: 'var(--red)', fontFamily: 'var(--font-mono)', marginBottom: 10 }}>Biggest Fallers</h2>
        {!fallers?.length ? (
          <div style={{ fontSize: '0.75rem', color: 'var(--text3)' }}>No picks on a downward trend right now.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {fallers.map(p => <PickCard key={p.pick_id} pick={p} compact />)}
          </div>
        )}
      </section>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function PickExchange() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeYear, setActiveYear] = useState(null);

  const load = async (refresh = false) => {
    setLoading(true);
    setError('');
    try {
      const url = `${API}/api/pick-exchange${refresh ? '?refresh=true' : ''}`;
      const r = await fetch(url);
      if (r.status === 404) {
        setError('Pick Exchange is not enabled.');
        setLoading(false);
        return;
      }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setData(d);
      if (!activeYear && d.future_years?.length) {
        setActiveYear(d.future_years[0]);
      }
    } catch (e) {
      setError(`Failed to load Pick Exchange: ${e.message}`);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div>
      <div className="page-header" style={{ marginBottom: 20 }}>
        <div className="page-title">Pick Exchange</div>
        <div className="page-subtitle">Reverse Max PF draft order · Live Sleeper pick ownership · Market value</div>
      </div>

      {loading && (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>
          <div style={{ fontSize: '2rem', marginBottom: 12 }}>📊</div>
          <div>Computing pick market...</div>
        </div>
      )}

      {!loading && error && (
        <div style={{ background: 'rgba(255,71,87,0.08)', border: '1px solid rgba(255,71,87,0.25)', borderRadius: 10, padding: '20px 24px', color: 'var(--red)' }}>
          {error}
        </div>
      )}

      {!loading && !error && data && (
        <>
          <div style={{ background: 'var(--bg2)', border: '1px solid var(--border)', borderLeft: '3px solid var(--accent)', borderRadius: 8, padding: '12px 14px', marginBottom: 16, color: 'var(--text2)', fontSize: '0.76rem', lineHeight: 1.55 }}>
            <strong style={{ color: 'var(--accent)' }}>Draft-order rule:</strong> {data.draft_order_methodology}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <button onClick={() => load(true)} style={{
              background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 6,
              color: 'var(--text2)', padding: '5px 14px', fontSize: '0.75rem', cursor: 'pointer',
            }}>↻ Refresh</button>
          </div>

          <MarketOverview overview={data.market_overview} />
          <BlueChips picks={data.blue_chips} />
          <RisersFallers risers={data.risers} fallers={data.fallers} />
          <PortfolioRankings portfolios={data.portfolios} />
          <RiskBoard riskBoard={data.risk_board} />
          {activeYear && (
            <DraftBoard
              draftBoard={data.draft_board}
              activeYear={activeYear}
              onYearChange={setActiveYear}
              futureYears={data.future_years}
            />
          )}
          <WarChests warChests={data.war_chests} />
        </>
      )}
    </div>
  );
}
